import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';
import {
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { ChevronRight } from 'lucide-react-native';
import { borderRadius, colors, spacing } from '@tabbio/tokens';
import { useTranslation } from 'react-i18next';
import { NativeButton } from '../common/NativeButton';
import { WebDialog } from '../common/WebDialog.web';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import { SkeletonBlock, SkeletonLine } from '../common/Skeleton.web';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';

class SheetContentBoundary extends React.Component<
  {
    children: React.ReactNode;
    textColor: string;
    borderColor: string;
    surfaceColor: string;
  },
  { hasError: boolean; message: string }
> {
  constructor(props: {
    children: React.ReactNode;
    textColor: string;
    borderColor: string;
    surfaceColor: string;
  }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown rendering error';
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown) {
    console.error('[ProfileSheetScaffold.web] render error', error);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <View
        style={[
          styles.errorBox,
          {
            borderColor: this.props.borderColor,
            backgroundColor: this.props.surfaceColor,
          },
        ]}
      >
        <Text style={[styles.errorTitle, { color: this.props.textColor }]}>
          This screen failed to render.
        </Text>
        <Text style={[styles.errorMessage, { color: this.props.textColor }]}>
          {this.state.message}
        </Text>
      </View>
    );
  }
}

export interface ProfileWebDialogShellProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  onClose: () => void;
  onBack?: () => void;
  maxWidth?: number;
  showHeader?: boolean;
}

export function ProfileSheetScaffold({
  title,
  subtitle,
  children,
  compact = false,
  maxContentWidth = 680,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  compact?: boolean;
  maxContentWidth?: number;
}) {
  const router = useRouter();
  const {
    routeMode,
    isHydrated,
    isLoading,
    loadError,
    isSaving,
    saveError,
    retry,
    clearSaveError,
  } = usePublicProfileBuilderModel();
  const { t } = useTranslation('common');
  const profileRoutes = getPublicProfileRoutes(routeMode);
  const showHydrationGate = !isHydrated && (isLoading || Boolean(loadError));
  const showStatusBanner = isHydrated && (isSaving || Boolean(saveError));

  const goBack = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  const closeDialog = useCallback(() => {
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  return (
    <WebDialog
      title={title}
      subtitle={subtitle}
      onClose={closeDialog}
      onBack={goBack}
      maxWidth={700}
    >
      <View
        style={[
          styles.contentContainer,
          { maxWidth: maxContentWidth },
          compact ? styles.contentContainerCompact : null,
        ]}
      >
        <View style={styles.contentWidth}>
          {showHydrationGate && isLoading && !loadError ? (
            <View
              style={[
                styles.statusCard,
                { backgroundColor: 'rgba(0,0,0,0.04)' as any, borderColor: 'var(--input-border)' as any, gap: 12 },
              ]}
            >
              <SkeletonLine width="48%" height={18} />
              <SkeletonBlock height={14} />
              <SkeletonBlock height={14} width="92%" />
              <SkeletonBlock height={14} width="74%" />
            </View>
          ) : null}
          {showHydrationGate && loadError ? (
            <View
              style={[
                styles.statusCard,
                { backgroundColor: 'rgba(0,0,0,0.04)' as any, borderColor: 'var(--input-border)' as any },
              ]}
            >
              <Text style={[styles.statusTitle, { color: 'var(--text-primary)' as any }]}>
                {t('publicProfile.status.errorTitle')}
              </Text>
              <Text style={[styles.statusBody, { color: 'var(--text-tertiary)' as any }]}>
                {loadError ?? t('publicProfile.status.errorBody')}
              </Text>
              {loadError ? (
                <NativeButton
                  title={t('actions.retry')}
                  onPress={() => {
                    void retry();
                  }}
                  variant="outline"
                  size="sm"
                  haptic="light"
                  style={styles.statusButton}
                />
              ) : null}
            </View>
          ) : null}
          {showStatusBanner ? (
            <View
              style={[
                styles.statusBanner,
                { backgroundColor: 'rgba(0,0,0,0.04)' as any, borderColor: 'var(--input-border)' as any },
              ]}
            >
              <View style={{ flex: 1, gap: 2 }}>
                <Text style={[styles.bannerTitle, { color: 'var(--text-primary)' as any }]}>
                  {isSaving
                    ? t('publicProfile.status.savingTitle')
                    : t('publicProfile.status.saveErrorTitle')}
                </Text>
                <Text style={[styles.bannerBody, { color: 'var(--text-tertiary)' as any }]}>
                  {isSaving
                    ? t('publicProfile.status.savingBody')
                    : saveError ?? t('publicProfile.status.saveErrorBody')}
                </Text>
              </View>
              {saveError ? (
                <NativeButton
                  title={t('actions.dismiss')}
                  onPress={clearSaveError}
                  variant="ghost"
                  size="sm"
                  haptic="light"
                />
              ) : null}
            </View>
          ) : null}
          <SheetContentBoundary
            textColor="var(--text-primary)"
            borderColor={'var(--input-border)' as any}
            surfaceColor={'rgba(0,0,0,0.04)' as any}
          >
            <View style={styles.contentBody}>{showHydrationGate ? null : children}</View>
          </SheetContentBoundary>
        </View>
      </View>
    </WebDialog>
  );
}

export interface ProfileSectionHeaderProps {
  title: string;
  style?: StyleProp<TextStyle>;
}

export function ProfileSectionHeader({ title, style }: ProfileSectionHeaderProps) {
  return (
    <Text style={[styles.sectionHeader, { color: 'var(--text-secondary)' as any }, style]}>
      {title}
    </Text>
  );
}

export interface ProfileGroupCardProps {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

export function ProfileGroupCard({ children, style }: ProfileGroupCardProps) {
  return (
    <View style={[styles.groupCard, { backgroundColor: 'rgba(0,0,0,0.04)' }, style]}>
      {children}
    </View>
  );
}

export interface ProfileGroupRowProps {
  icon?: React.ReactNode;
  label: string;
  value?: string;
  description?: string;
  onPress?: () => void;
  rightElement?: React.ReactNode;
  showChevron?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  isLast?: boolean;
  labelStyle?: StyleProp<TextStyle>;
}

export function ProfileGroupRow({
  icon,
  label,
  value,
  description,
  onPress,
  rightElement,
  showChevron = false,
  destructive = false,
  disabled = false,
  isLast = false,
  labelStyle,
}: ProfileGroupRowProps) {
  const textColor = destructive ? '#d93025' : 'var(--text-primary)';
  const rowDisabled = disabled;

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress || rowDisabled}
      style={({ pressed }) => [
        styles.groupRow,
        !isLast
          ? ({
              borderBottomWidth: StyleSheet.hairlineWidth,
              borderBottomColor: 'var(--input-border)',
            } as any)
          : null,
        onPress && pressed ? ({ backgroundColor: 'rgba(0,0,0,0.06)' } as any) : null,
      ]}
    >
      {icon ? <View style={styles.groupRowIcon}>{icon}</View> : null}
      <View style={styles.groupRowContent}>
        <Text
          style={[
            styles.groupRowLabel,
            {
              color: (rowDisabled ? 'var(--text-tertiary)' : textColor) as any,
            },
            labelStyle,
          ]}
        >
          {label}
        </Text>
        {description ? (
          <Text
            style={[
              styles.groupRowDescription,
              { color: 'var(--text-tertiary)' as any },
            ]}
          >
            {description}
          </Text>
        ) : null}
      </View>
      <View style={styles.groupRowRight}>
        {rightElement}
        {value ? (
          <Text
            style={[
              styles.groupRowValue,
              { color: 'var(--text-tertiary)' as any },
            ]}
          >
            {value}
          </Text>
        ) : null}
        {showChevron ? (
          <ChevronRight size={17} color="var(--text-tertiary)" strokeWidth={1.9} />
        ) : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    paddingTop: spacing[3],
    paddingBottom: spacing[5],
    width: '100%',
    alignSelf: 'center',
  },
  contentContainerCompact: {
    paddingTop: spacing[2],
  },
  contentWidth: {
    width: '100%',
  },
  contentBody: {
    gap: spacing[2],
  },
  sectionHeader: {
    fontSize: 13,
    fontWeight: '500',
    marginTop: spacing[1],
    marginBottom: spacing[1],
    paddingHorizontal: spacing[1],
  },
  groupCard: {
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
  },
  groupRow: {
    minHeight: 54,
    paddingHorizontal: spacing[4],
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  groupRowIcon: {
    width: 28,
    alignItems: 'center',
    marginRight: 12,
  },
  groupRowContent: {
    flex: 1,
    gap: 2,
  },
  groupRowLabel: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 20,
  },
  groupRowDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  groupRowRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  groupRowValue: {
    fontSize: 14,
    lineHeight: 18,
  },
  errorBox: {
    borderWidth: 1,
    borderRadius: borderRadius.lg,
    padding: spacing[3],
    gap: 8,
  },
  errorTitle: {
    fontSize: 15,
    fontWeight: '700',
  },
  errorMessage: {
    fontSize: 13,
    lineHeight: 18,
  },
  statusCard: {
    borderRadius: borderRadius.xl,
    borderWidth: StyleSheet.hairlineWidth,
    padding: spacing[4],
    gap: spacing[2],
    marginBottom: spacing[2],
  },
  statusTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  statusBody: {
    fontSize: 14,
    lineHeight: 20,
  },
  statusButton: {
    alignSelf: 'flex-start',
  },
  statusBanner: {
    borderRadius: borderRadius.xl,
    borderWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2],
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    marginBottom: spacing[2],
  },
  bannerTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  bannerBody: {
    fontSize: 12,
    lineHeight: 17,
  },
});
